import React, { useEffect } from 'react';
import { Outlet, useLocation, useParams } from '@tanstack/react-router';
import { errorBoundary, withInject } from '@piwikpro/platform';
import { ListSidebarPreference } from '@piwikpro/user-preferences';
import {
  View,
  SplashScreen,
  Stack,
  Button,
  Tabs,
  Spinner,
  Tooltip,
  Text,
  TFunction,
} from '@piwikpro/ui-components';
import TitleComponent from '@piwikpro/title-crate/Title';
import { AuthSettings } from '@piwikpro/auth-crate';
import { UserGroups, UserGroup } from '@piwikpro/user-groups-crate';
import { OrganizationAuthSettingsState } from '@piwikpro/auth-crate/reducers/auth';
import { IUserGroupDetailsState } from '@piwikpro/user-groups-crate/redux/details';
import { RouterService } from '@piwikpro/router-crate';
import classNameBind from 'classnames/bind';
import UserGroupDetailsOverlay from './UserGroupDetailsOverlay';
import styles from './UserGroupDetails.module.css';

const bindClasses = classNameBind.bind(styles);
const ASCENDING = '';
const DESCENDING = '-';

interface UserGroupDetailsProps {
  config: any
  userPreferences: any
  groupId: string
  routerService: RouterService
  userGroups: UserGroups
  userGroup: UserGroup
  authSettings: AuthSettings
  isSamlEnabled: OrganizationAuthSettingsState['saml']['enabled']
  isAutoProvisioningEnabled: OrganizationAuthSettingsState['autoProvisioning']['enabled']
  failed: IUserGroupDetailsState['failed']
  isLoading: IUserGroupDetailsState['isLoading']
  Title: typeof TitleComponent
  t: TFunction
  filters: any
}

const UserGroupDetails: React.FC<UserGroupDetailsProps> = ({
  config,
  userPreferences,
  authSettings,
  isSamlEnabled,
  isAutoProvisioningEnabled,
  userGroups,
  userGroup,
  isLoading,
  failed,
  Title,
  t,
  filters,
  groupId,
  routerService,
}) => {
  const shouldShowExternalIdpInfo = Boolean(
    isSamlEnabled && isAutoProvisioningEnabled && userGroup && userGroup.attributes.external_id,
  );
  const location = useLocation();
  const listSidebarPreference = new ListSidebarPreference(
    userPreferences.load(),
    '/administration/user-groups',
    {
      sortBy: config.get('USER_GROUP_DEFAULT_SORT'),
    },
  );

  const fetchOrgAuthSettings = async () => {
    try {
      await authSettings.getSettingsForOrganization();
    // eslint-disable-next-line no-empty
    } catch {}
  };

  const fetchUserGroupDetails = async () => {
    if (groupId) {
      try {
        await userGroups.findOne(groupId);
        // eslint-disable-next-line no-empty
      } catch (err) { }
    }
  };

  useEffect(() => {
    fetchOrgAuthSettings();
  }, []);

  useEffect(() => {
    fetchUserGroupDetails();
  }, [userGroups, groupId]);

  const moveToRenameView = () => {
    routerService.openAdministrationOverlay('/m/rename');
  };

  const moveToAddExternalIDView = () => {
    routerService.openAdministrationOverlay('/o/add-external-id');
  };

  const moveToAddUsersToGroupView = () => {
    routerService.openAdministrationOverlay('/o/add-users');
  };

  const deleteUserGroup = async () => {
    try {
      await userGroups.delete(userGroup.id, userGroup.attributes.name, shouldShowExternalIdpInfo);
      userGroups.find({
        sort: {
          direction: listSidebarPreference.sortBy === 'name' ? ASCENDING : DESCENDING,
          by: listSidebarPreference.sortBy,
        },
        search: '',
      });
      routerService.push({ to: '/administration/user-groups' });
      // eslint-disable-next-line no-empty
    } catch (e) { }
  };

  if (isLoading) {
    return <Spinner type="centered" className="x-large" />;
  }

  if (failed) {
    return <SplashScreen type="notFound" />;
  }

  const tabs = [
    {
      id: 'members',
      label: t('user-groups.details.members.title'),
      value: `/administration/user-groups/${groupId}/members`,
    },
    {
      id: 'permissions',
      label: t('user-groups.details.permissions.title'),
      value: `/administration/user-groups/${groupId}/permissions`,
    },
  ];
  const externalId = userGroup.attributes.external_id;

  return (
    <Title part={userGroup.attributes.name} important>
      <View>
        <View.Header
          caption={(
            <div className={bindClasses('button-container')} style={{ width: '100%' }}>
              <Stack vertical spacing="narrow">
                <Stack.Item fill>
                  <Text as="p" size={12} color="InkLighter" dataId="ViewHeader.caption">
                    {t('user-groups.details.subTitle', { id: userGroup.id }) as string}
                  </Text>
                </Stack.Item>
                <Stack.Item fill>
                  <Stack spacing="narrow" verticalAlignment="center">
                    <Stack.Item fillLight>
                      <Tooltip content={externalId}>
                        <Text as="p" size={12} color="InkLighter" wrapType="noWrapAndCutText" dataId="ViewHeader.caption">
                          {t('user-groups.details.subTitle-external-id') as string}
                          {' '}
                          {externalId}
                        </Text>
                      </Tooltip>
                    </Stack.Item>
                    {!externalId ? (
                      <Stack.Item previousSpacing="extraNarrow">
                        <Button onClick={moveToAddExternalIDView} size="tiny" minimalWidth appearance="simple" text={t('user-groups.details.assign-external-id-button')} />
                      </Stack.Item>
                    ) : (
                      <>
                        <Stack.Item>
                          <Button
                            onClick={moveToAddExternalIDView}
                            size="tiny"
                            appearance="simple"
                            icon="edit"
                            ariaLabel={t('user-groups.details.edit-external-id-button')}
                          />
                        </Stack.Item>
                        <Stack.Item>
                          <Button
                            size="tiny"
                            appearance="simple"
                            icon="trash"
                            ariaLabel={t('user-groups.details.remove-external-id-button')}
                            onClick={
                              async () => {
                                try {
                                  await userGroups.update(userGroup.id, {
                                    external_id: null,
                                    name: userGroup.attributes.name,
                                  });
                                  await userGroups.find(filters);
                                  await fetchUserGroupDetails();
                                  // eslint-disable-next-line no-empty
                                } catch {}
                              }}
                          />
                        </Stack.Item>
                      </>
                    )}
                  </Stack>
                </Stack.Item>
              </Stack>
            </div>
          )}
          title={userGroup.attributes.name}
        >
          <Button appearance="primary" text={t('user-groups.actions.add-members')} onClick={moveToAddUsersToGroupView} />
          <Button appearance="default" text={t('user-groups.actions.rename-user-group')} onClick={moveToRenameView} />
          <Button appearance="default" text={t('user-groups.actions.delete-user-group')} onClick={deleteUserGroup} />
        </View.Header>
        <View.Body>
          <Stack vertical horizontalAlignment="fill" spacing="wide">
            <Stack.Item>
              <Tabs
                tabs={tabs}
                RouterLink={null}
                active={location.pathname.indexOf('/members') !== -1 ? tabs[0].value : tabs[1].value}
                setActive={(tab: 'members' | 'permissions') => {
                  if (tab.includes('/members')) {
                    routerService.push({
                      to: '/administration/user-groups/$groupId/members',
                      params: { groupId },
                    });
                    return;
                  }
                  routerService.push({
                    to: '/administration/user-groups/$groupId/permissions',
                    params: { groupId },
                  });
                }}
              />
            </Stack.Item>
            <Stack.Item>
              <Outlet />
            </Stack.Item>
          </Stack>
          <UserGroupDetailsOverlay />
        </View.Body>
      </View>
    </Title>
  );
};

const UserGroupDetailsConnected = withInject({
  config: 'config',
  authSettings: 'AuthCrate.authSettings',
  userPreferences: 'userPreferences',
  userGroups: 'UserGroupsCrate.userGroups',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(
  errorBoundary(UserGroupDetails),
);

const UserGroupDetailsWrapper = (props) => {
  const params = useParams({ from: '/_secured/administration/user-groups/$groupId' });
  return <UserGroupDetailsConnected {...props} groupId={params.groupId} />;
};

export default UserGroupDetailsWrapper;
